import {
  ArrowDownIcon,
  ArrowUpIcon,
  DocumentArrowUpIcon,
  DocumentDuplicateIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  type IBudgetTemplateAiImportResult,
  type IBudgetTemplateConfig,
  type IBudgetTemplatesConfig,
  type IBudgetTemplateSection,
  addBudgetTemplateConfig,
  deleteBudgetTemplateConfig,
  getBudgetTemplatesConfig,
  setBudgetTemplatesConfig,
  setDefaultBudgetTemplateConfig,
} from 'services'
import { Button, Select, Tooltip } from 'stories/components'
import { confirm, prompt } from 'utils'

import { BudgetTemplateAiImportModal } from './AiImportModal'
import {
  BATHROOM_SECTION_KEY,
  BUDGET_COST_TYPES,
  slugifyBudgetKey,
  slugifyTemplateKey,
  uniqueBudgetKey,
} from './constants'

/**
 * Admin > Configuration > Budget Templates.
 *
 * A tenant used to get one fixed Budget & Scope of Work layout. This screen manages any number of
 * named layouts; the one flagged Default is what new budget forms are built from, and an operator
 * can switch an individual loan to another one from the Budget & Scope of Work screen.
 */
export const BudgetTemplates = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<IBudgetTemplatesConfig>({})
  const [selectedKey, setSelectedKey] = useState('')
  const [isChanged, setChanged] = useState(false)
  const [isAiImportOpen, setAiImportOpen] = useState(false)

  const orderedKeys = useMemo(
    () => Object.keys(data).sort((a, b) => (data[a].order ?? 0) - (data[b].order ?? 0)),
    [data],
  )

  const selected: IBudgetTemplateConfig | undefined = data[selectedKey]

  const loadData = (keepKey = '') => {
    setLoading(true)
    return getBudgetTemplatesConfig()
      .then((config) => {
        setData(config)
        const keys = Object.keys(config).sort((a, b) => (config[a].order ?? 0) - (config[b].order ?? 0))
        setSelectedKey(keepKey && config[keepKey] ? keepKey : keys[0] || '')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadData()
  }, [])

  const onSubmit = () => {
    setLoading(true)
    setBudgetTemplatesConfig(data)
      .then(() => {
        setChanged(false)
        toast('Budget Templates have been saved.', { type: 'info' })
      })
      .catch((e: any) => toast(e?.message || 'Failed to save budget templates.', { type: 'error' }))
      .finally(() => setLoading(false))
  }

  const updateSelected = (updater: (template: IBudgetTemplateConfig) => void) => {
    const next = cloneDeep(data)
    updater(next[selectedKey])
    setData(next)
    setChanged(true)
  }

  const onRename = (key: string, title: string) => {
    if (!title.trim()) return
    const next = cloneDeep(data)
    next[key].title = title.trim()
    setData(next)
    setChanged(true)
  }

  const onAdd = async (sourceKey?: string) => {
    const title = await prompt(sourceKey ? 'Enter a name for the copy:' : 'Enter a name for the new budget template:', {
      required: true,
    })
    if (!title?.trim()) return

    setLoading(true)
    try {
      const { key } = await addBudgetTemplateConfig(title.trim(), sourceKey)
      setChanged(false)
      await loadData(key)
      toast(`Budget template "${title.trim()}" has been created.`, { type: 'info' })
    } catch (e: any) {
      toast(e?.message || 'Failed to create budget template.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  /**
   * Save a spreadsheet-derived template straight away, the way "Add Budget Template" does.
   *
   * This used to only stage it in local state and wait for Save Changes, which reads as "the import
   * worked" while a refresh silently throws the template away. Any edits already pending on the
   * screen go up with it rather than being discarded by the reload that follows.
   */
  const onApplyAiImport = async (title: string, imported: IBudgetTemplateAiImportResult) => {
    const next = cloneDeep(data)
    const key = uniqueBudgetKey(slugifyTemplateKey(title) || 'budget_template', Object.keys(next))

    next[key] = {
      title,
      order: Object.keys(next).length,
      isDefault: false,
      sections: imported.sections,
      itemMapping: imported.itemMapping,
    }

    setLoading(true)
    try {
      await setBudgetTemplatesConfig(next)
      setChanged(false)
      await loadData(key)
      setAiImportOpen(false)
      toast(`Budget template "${title}" has been created from the spreadsheet.`, { type: 'info' })
    } catch (e: any) {
      toast(e?.message || 'Failed to save the imported budget template.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onDelete = async (key: string) => {
    const title = data[key]?.title || key
    const ok = await confirm(
      `Delete budget template "${title}"?\n\nLoans already using it fall back to the default template the next time their budget is opened.`,
    )
    if (!ok) return

    setLoading(true)
    try {
      await deleteBudgetTemplateConfig(key)
      setChanged(false)
      await loadData()
      toast(`Budget template "${title}" has been deleted.`, { type: 'info' })
    } catch (e: any) {
      toast(e?.message || 'Failed to delete budget template.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onSetDefault = async (key: string) => {
    setLoading(true)
    try {
      await setDefaultBudgetTemplateConfig(key)
      setChanged(false)
      await loadData(key)
      toast(`"${data[key]?.title || key}" is now the default budget template.`, { type: 'info' })
    } catch (e: any) {
      toast(e?.message || 'Failed to set the default budget template.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onAddSection = async () => {
    const label = await prompt('Enter a name for the new section:', { required: true })
    if (!label?.trim()) return

    updateSelected((template) => {
      const key = uniqueBudgetKey(
        slugifyBudgetKey(label) || 'section',
        template.sections.map((v) => v.value),
      )
      template.sections.push({
        label: label.trim().toUpperCase(),
        value: key,
        defaultCostType: BUDGET_COST_TYPES[1],
        items: [],
      })
    })
  }

  const onRemoveSection = async (index: number) => {
    const section = selected?.sections[index]
    const ok = await confirm(`Remove the section "${section?.label}" and its ${section?.items.length || 0} line items?`)
    if (!ok) return

    updateSelected((template) => {
      template.sections.splice(index, 1)
    })
  }

  const onMoveSection = (index: number, offset: number) => {
    updateSelected((template) => {
      const target = index + offset
      if (target < 0 || target >= template.sections.length) return
      const [section] = template.sections.splice(index, 1)
      template.sections.splice(target, 0, section)
    })
  }

  const onChangeSection = (index: number, field: keyof IBudgetTemplateSection, value: string) => {
    updateSelected((template) => {
      ;(template.sections[index] as any)[field] = value
    })
  }

  const onAddLine = async (sectionIndex: number) => {
    const label = await prompt('Enter a name for the new line item:', { required: true })
    if (!label?.trim()) return

    updateSelected((template) => {
      const takenKeys = template.sections.reduce<string[]>((acc, section) => [...acc, ...section.items], [])
      const key = uniqueBudgetKey(slugifyBudgetKey(label) || 'lineItem', takenKeys)
      template.sections[sectionIndex].items.push(key)
      template.itemMapping = { ...template.itemMapping, [key]: label.trim() }
    })
  }

  const onRenameLine = (key: string, label: string) => {
    if (!label.trim()) return
    updateSelected((template) => {
      template.itemMapping = { ...template.itemMapping, [key]: label.trim() }
    })
  }

  const onRemoveLine = (sectionIndex: number, key: string) => {
    updateSelected((template) => {
      template.sections[sectionIndex].items = template.sections[sectionIndex].items.filter((v) => v !== key)
    })
  }

  const onMoveLine = (sectionIndex: number, index: number, offset: number) => {
    updateSelected((template) => {
      const items = template.sections[sectionIndex].items
      const target = index + offset
      if (target < 0 || target >= items.length) return
      const [key] = items.splice(index, 1)
      items.splice(target, 0, key)
    })
  }

  const lineCount = (template: IBudgetTemplateConfig) =>
    template.sections.reduce((count, section) => count + section.items.length, 0)

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />

      <p className="text-sm text-gray-500 mb-4">
        Budget layouts offered on the Budget &amp; Scope of Work screen. New budget forms are built from the template
        marked Default; an operator can switch a single loan to any other template from that screen.
      </p>

      <table className="text-sm w-full mb-8">
        <thead className="uppercase text-xs bg-gray-50 dark:bg-gray-700">
          <tr>
            {['No', 'Name', 'Default', 'Sections', 'Line Items', 'Actions'].map((v) => (
              <th scope="col" className="p-3 border" key={`header-${v}`}>
                {v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orderedKeys.map((key, index) => {
            const template = data[key]
            return (
              <tr
                key={key}
                className={`cursor-pointer ${selectedKey === key ? 'bg-shade-blue/10' : ''}`}
                onClick={() => setSelectedKey(key)}
              >
                <td className="p-2 border">{index + 1}</td>
                <td className="px-3 border">
                  <PlainInput
                    value={template.title}
                    content={template.title}
                    onChange={(value: string) => onRename(key, value)}
                  />
                </td>
                <td className="px-3 border text-center">
                  <input
                    type="radio"
                    id={`budget-template-default-${key}`}
                    name="budget-template-default"
                    checked={!!template.isDefault}
                    onChange={() => onSetDefault(key)}
                  />
                </td>
                <td className="px-3 border text-center">{template.sections.length}</td>
                <td className="px-3 border text-center">{lineCount(template)}</td>
                <td className="px-3 border text-center">
                  <div className="inline-flex items-center justify-center gap-1">
                    <Tooltip message="Copy Template">
                      <span
                        className="btn-icon inline-flex"
                        onClick={(e) => {
                          e.stopPropagation()
                          onAdd(key)
                        }}
                      >
                        <DocumentDuplicateIcon className="w-4 h-4 text-gray-500" />
                      </span>
                    </Tooltip>
                    {!template.isDefault && orderedKeys.length > 1 && (
                      <Tooltip message="Delete Template">
                        <span
                          className="btn-icon inline-flex"
                          onClick={(e) => {
                            e.stopPropagation()
                            onDelete(key)
                          }}
                        >
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Button onClick={() => onAdd()}>
          <div className="flex items-center">
            <PlusIcon className="h-4 w-4 mr-1" />
            <span>Add Budget Template</span>
          </div>
        </Button>
        <Button color="gray" onClick={() => setAiImportOpen(true)}>
          <div className="flex items-center">
            <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
            <span>Import from Spreadsheet</span>
          </div>
        </Button>
      </div>

      {selected && (
        <div className="border rounded p-4">
          <h3 className="text-lg font-semibold mb-1">{selected.title}</h3>
          <p className="text-sm text-gray-500 mb-4">
            Sections and their line items, in the order they appear on the budget. A section keyed
            <span className="font-mono mx-1">{BATHROOM_SECTION_KEY}</span>
            is repeated once per bathroom on the loan.
          </p>

          {selected.sections.map((section, sectionIndex) => (
            <div className="border rounded mb-4" key={`${selectedKey}-${section.value}`}>
              <div className="flex flex-wrap items-center gap-3 bg-gray-50 px-3 py-2 border-b">
                <div className="flex-1 min-w-[200px]">
                  <PlainInput
                    value={section.label}
                    content={section.label}
                    onChange={(value: string) => onChangeSection(sectionIndex, 'label', value)}
                  />
                  <div className="text-xs text-gray-400 font-mono">{section.value}</div>
                </div>
                <div className="w-[170px]">
                  <Select
                    id={`cost-type-${selectedKey}-${section.value}`}
                    title="Default Cost Type"
                    options={BUDGET_COST_TYPES}
                    value={section.defaultCostType}
                    onChange={(value: string) => onChangeSection(sectionIndex, 'defaultCostType', value)}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Tooltip message="Move Up">
                    <span className="btn-icon inline-flex" onClick={() => onMoveSection(sectionIndex, -1)}>
                      <ArrowUpIcon className="w-4 h-4 text-gray-500" />
                    </span>
                  </Tooltip>
                  <Tooltip message="Move Down">
                    <span className="btn-icon inline-flex" onClick={() => onMoveSection(sectionIndex, 1)}>
                      <ArrowDownIcon className="w-4 h-4 text-gray-500" />
                    </span>
                  </Tooltip>
                  <Tooltip message="Remove Section">
                    <span className="btn-icon inline-flex" onClick={() => onRemoveSection(sectionIndex)}>
                      <TrashIcon className="w-4 h-4 text-red-500" />
                    </span>
                  </Tooltip>
                </div>
              </div>

              <table className="text-sm w-full">
                <tbody>
                  {section.items.map((key, index) => (
                    <tr key={`${section.value}-${key}`}>
                      <td className="px-3 py-1 border-b w-[40px] text-gray-400">{index + 1}</td>
                      <td className="px-3 py-1 border-b">
                        <PlainInput
                          value={selected.itemMapping?.[key] || key}
                          content={selected.itemMapping?.[key] || key}
                          onChange={(value: string) => onRenameLine(key, value)}
                        />
                      </td>
                      <td className="px-3 py-1 border-b w-[220px] text-xs text-gray-400 font-mono">{key}</td>
                      <td className="px-3 py-1 border-b w-[120px] text-center">
                        <div className="inline-flex items-center gap-1">
                          <Tooltip message="Move Up">
                            <span className="btn-icon inline-flex" onClick={() => onMoveLine(sectionIndex, index, -1)}>
                              <ArrowUpIcon className="w-4 h-4 text-gray-500" />
                            </span>
                          </Tooltip>
                          <Tooltip message="Move Down">
                            <span className="btn-icon inline-flex" onClick={() => onMoveLine(sectionIndex, index, 1)}>
                              <ArrowDownIcon className="w-4 h-4 text-gray-500" />
                            </span>
                          </Tooltip>
                          <Tooltip message="Remove Line Item">
                            <span className="btn-icon inline-flex" onClick={() => onRemoveLine(sectionIndex, key)}>
                              <TrashIcon className="w-4 h-4 text-red-500" />
                            </span>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="px-3 py-2" colSpan={4}>
                      <span
                        className="text-shade-blue text-sm cursor-pointer hover:underline inline-flex items-center"
                        onClick={() => onAddLine(sectionIndex)}
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Line Item
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          <Button onClick={onAddSection}>
            <div className="flex items-center">
              <PlusIcon className="h-4 w-4 mr-1" />
              <span>Add Section</span>
            </div>
          </Button>
        </div>
      )}

      {isAiImportOpen && (
        <BudgetTemplateAiImportModal
          isOpen={isAiImportOpen}
          existingTitles={orderedKeys.map((key) => data[key].title)}
          onClose={() => setAiImportOpen(false)}
          onApply={onApplyAiImport}
        />
      )}
    </div>
  )
}
